import React, { createContext, useContext, useState, ReactNode } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  CircularProgress,
  styled,
  Breakpoint,
  SxProps,
} from '@mui/material';

interface ModalContextType {
  showModal: (options: ModalOptions) => void;
  hideModal: () => void;
}

interface ModalOptions {
  content: ReactNode;
  title?: string;
  confirmText?: string;
  switchLayout?: boolean;
  cancelText?: string;
  onConfirm?: () => Promise<void> | void;
  onCancel?: () => Promise<void> | void;
  onDismiss?: () => void;
  cancelButtonTheme?: 'error' | 'default';
  width?: string;
  modalStyles?: SxProps;
  disableBackdropClick?: boolean;
}

const LoadingButton = styled(Button)(() => ({
  position: 'relative',
  textTransform: 'none',
  '& .MuiCircularProgress-root': {
    position: 'absolute',
    left: '50%',
    marginLeft: -12,
  },
}));

export const ModalContext = createContext<ModalContextType | undefined>(
  undefined,
);

export const ModalProvider: React.FC<{ children: ReactNode }> = ({
  children,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [modalContent, setModalContent] = useState<ReactNode | null>(null);
  const [modalTitle, setModalTitle] = useState<string | undefined>();
  const [confirmText, setConfirmText] = useState<string | undefined>();
  const [cancelText, setCancelText] = useState<string | undefined>();
  const [currentModalSx, setCurrentModalSx] = useState<SxProps | undefined>();
  const [switchLayout, setSwitchLayout] = useState<boolean>();
  const [width, setWidth] = useState<string>();
  const [onConfirm, setOnConfirm] = useState<
    (() => Promise<void> | void) | undefined
  >();
  const [onCancel, setOnCancel] = useState<
    (() => Promise<void> | void) | undefined
  >();
  const [onDismiss, setOnDismiss] = useState<(() => void) | undefined>();
  const [isConfirmLoading, setIsConfirmLoading] = useState(false);
  const [isCancelLoading, setIsCancelLoading] = useState(false);
  const [cancelButtonTheme, setCancelButtonTheme] = useState<
    'error' | 'default'
  >('default');
  const [disableBackdropClick, setDisableBackdropClick] = useState(false);

  const showModal = ({
    content,
    title,
    confirmText,
    cancelText,
    onConfirm: confirmHandler,
    onCancel: cancelHandler,
    onDismiss: dismissHandler,
    cancelButtonTheme = 'default',
    width = 'sm',
    switchLayout = false,
    modalStyles = {},
    disableBackdropClick: disableBackdrop = false,
  }: ModalOptions) => {
    // prevents race conditions where a new modal inherits the previous modal's loading state
    handleExited();

    setModalContent(content);
    setModalTitle(title);
    setConfirmText(confirmText);
    setCancelText(cancelText);
    setOnConfirm(() => confirmHandler);
    setOnCancel(() => cancelHandler);
    setOnDismiss(() => dismissHandler);
    setCancelButtonTheme(cancelButtonTheme);
    setWidth(width);
    setSwitchLayout(switchLayout);
    setIsOpen(true);
    setCurrentModalSx(modalStyles);
    setDisableBackdropClick(disableBackdrop);
  };

  const hideModal = () => {
    setIsOpen(false);
    // Content will be cleared in handleExited after animation completes
  };

  const handleExited = () => {
    // Clear all modal state after the exit animation completes
    setModalContent(null);
    setModalTitle(undefined);
    setConfirmText(undefined);
    setCancelText(undefined);
    setOnConfirm(undefined);
    setOnCancel(undefined);
    setOnDismiss(undefined);
    setWidth('md');
    setIsConfirmLoading(false);
    setIsCancelLoading(false);
    setSwitchLayout(false);
    setCancelButtonTheme('default');
    setCurrentModalSx(undefined);
    setDisableBackdropClick(false);
  };

  const handleConfirm = async () => {
    if (!onConfirm) {
      hideModal();
      return;
    }

    try {
      setIsConfirmLoading(true);
      await onConfirm();
      hideModal();
    } catch (error) {
      console.error('Error in confirm handler:', error);
      setIsConfirmLoading(false);
    }
  };

  const handleCancel = async () => {
    if (!onCancel) {
      hideModal();
      return;
    }

    try {
      setIsCancelLoading(true);
      await onCancel();
      hideModal();
    } catch (error) {
      console.error('Error in cancel handler:', error);
      setIsCancelLoading(false);
    }
  };

  const handleDismiss = (
    event: object,
    reason: 'backdropClick' | 'escapeKeyDown',
  ) => {
    // Prevent closing on backdrop click if disabled
    if (disableBackdropClick && reason === 'backdropClick') {
      return;
    }

    // Only handle dismiss if we're not in a loading state
    if (!isConfirmLoading && !isCancelLoading) {
      if (onDismiss) {
        onDismiss();
      }
      hideModal();
    }
  };

  const isLoading = isConfirmLoading || isCancelLoading;

  return (
    <ModalContext.Provider value={{ showModal, hideModal }}>
      {children}
      <Dialog
        open={isOpen}
        onClose={handleDismiss}
        maxWidth={width as Breakpoint}
        fullWidth
        sx={{
          ...currentModalSx,
        }}
        slotProps={{
          transition: {
            onExited: handleExited,
          },
        }}
      >
        {modalTitle && <DialogTitle>{modalTitle}</DialogTitle>}
        <DialogContent>{modalContent}</DialogContent>
        {(confirmText || cancelText) && (
          <DialogActions>
            {cancelText && (
              <LoadingButton
                onClick={handleCancel}
                disabled={isLoading}
                color={cancelButtonTheme === 'error' ? 'error' : 'primary'}
                variant={
                  cancelButtonTheme === 'error' || switchLayout
                    ? 'contained'
                    : 'text'
                }
              >
                {isCancelLoading && <CircularProgress size={24} />}
                <span
                  style={{ visibility: isCancelLoading ? 'hidden' : 'visible' }}
                >
                  {cancelText}
                </span>
              </LoadingButton>
            )}
            {confirmText && (
              <LoadingButton
                onClick={handleConfirm}
                disabled={isLoading}
                variant={switchLayout ? 'text' : 'contained'}
                color="primary"
              >
                {isConfirmLoading && <CircularProgress size={24} />}
                <span
                  style={{
                    visibility: isConfirmLoading ? 'hidden' : 'visible',
                  }}
                >
                  {confirmText}
                </span>
              </LoadingButton>
            )}
          </DialogActions>
        )}
      </Dialog>
    </ModalContext.Provider>
  );
};

export const useModal = () => {
  const context = useContext(ModalContext);
  if (context === undefined) {
    throw new Error('useModal must be used within a ModalProvider');
  }
  return context;
};
